import { FormEvent, useMemo, useState } from "react";
import {
  ArrowRight,
  ArrowUpRight,
  Check,
  CircleHelp,
  FileSearch,
  Layers3,
  Menu,
  ShieldCheck,
  Signal,
  X,
} from "lucide-react";
import { FREQUENTLY_ASKED_QUESTIONS, PORTAL_URL } from "./site";

type PublicPage = "home" | "contact" | "not-found";

function currentPage(): PublicPage {
  const path = window.location.pathname.replace(/\/+$/, "") || "/";
  if (path === "/") return "home";
  if (path === "/contact") return "contact";
  return "not-found";
}

function BrandMark() {
  return (
    <a className="brand" href="/" aria-label="Broadview home">
      <span className="brand-mark" aria-hidden="true"><span /></span>
      <span>Broadview</span>
    </a>
  );
}

function Header({ page }: { page: PublicPage }) {
  const [open, setOpen] = useState(false);
  const homeLink = page === "home" ? "#how-it-works" : "/#how-it-works";

  return (
    <header className="site-header">
      <div className="shell nav-shell">
        <BrandMark />
        <button className="menu-toggle" type="button" aria-label={open ? "Close navigation" : "Open navigation"} aria-expanded={open} onClick={() => setOpen((value) => !value)}>
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
        <nav className={open ? "nav-links open" : "nav-links"} aria-label="Primary navigation">
          <a href={homeLink} onClick={() => setOpen(false)}>How it works</a>
          <a href={page === "home" ? "#answers" : "/#answers"} onClick={() => setOpen(false)}>Answers</a>
          <a href="/contact/" aria-current={page === "contact" ? "page" : undefined} onClick={() => setOpen(false)}>Contact</a>
          <a className="nav-portal" href={PORTAL_URL}>Subscriber sign in <ArrowUpRight size={14} /></a>
        </nav>
      </div>
    </header>
  );
}

function Eyebrow({ children }: { children: string }) {
  return <p className="eyebrow"><span />{children}</p>;
}

function Hero() {
  return (
    <section className="hero shell">
      <div className="hero-copy">
        <Eyebrow>Evidence-led warranty intelligence</Eyebrow>
        <h1>See the signal.<br /><em>Keep the evidence.</em></h1>
        <p className="hero-intro">Broadview gives warranty, quality, service, and operations teams a shared way to organize evidence, prioritize review, and carry accountable context into action.</p>
        <div className="hero-actions">
          <a className="button primary" href="#how-it-works">See how Broadview works <ArrowRight size={17} /></a>
          <a className="text-link" href="/contact/">Start a conversation <ArrowUpRight size={16} /></a>
        </div>
        <p className="hero-note">Designed to support disciplined review—not automated safety, regulatory, or customer decisions.</p>
      </div>
      <div className="hero-visual" aria-label="Illustrative evidence, risk signal, and review workflow">
        <div className="visual-topline"><span>Portfolio signal</span><span>Evidence posture</span></div>
        <div className="signal-grid" aria-hidden="true">
          <i /><i /><i /><i /><i /><i /><i /><i />
        </div>
        <div className="signal-trace" aria-hidden="true"><b /><b /><b /><b /><b /><b /></div>
        <div className="evidence-card">
          <div className="evidence-card-top"><span className="status-dot" /> Evidence set <span>01</span></div>
          <strong>Driveline vibration</strong>
          <p>Claim pattern · service notes · component context</p>
          <div className="evidence-rule" /><div className="evidence-rule short" />
          <span className="state-chip">Review ready</span>
        </div>
        <div className="risk-marker marker-one"><span>1</span><small>Priority review</small></div>
        <div className="risk-marker marker-two"><span>2</span><small>Evidence linked</small></div>
        <div className="visual-caption">Illustrative workflow context · not customer data</div>
      </div>
    </section>
  );
}

const principles = [
  {
    icon: Signal,
    number: "01",
    title: "Start with the portfolio",
    detail: "Bring approved warranty and adjacent signals into a focused operating view instead of chasing one disconnected anecdote at a time.",
  },
  {
    icon: FileSearch,
    number: "02",
    title: "Prioritize with evidence in view",
    detail: "Compare issues through explainable ranking views, then drill into the source context that supports the question—not a black-box answer.",
  },
  {
    icon: Layers3,
    number: "03",
    title: "Move into accountable work",
    detail: "Use a governed Problem Record to keep scope, evidence, ownership, action, and approval context together through review.",
  },
];

function Principles() {
  return (
    <section className="section section-ink" id="how-it-works">
      <div className="shell">
        <div className="section-heading split-heading">
          <div><Eyebrow>How Broadview works</Eyebrow><h2>One operating thread from<br /><em>signal to accountable action.</em></h2></div>
          <p>Broadview is designed around a simple premise: when the evidence, the priority, and the workflow stay connected, teams can review emerging warranty issues with more shared context.</p>
        </div>
        <div className="principles-grid">
          {principles.map(({ icon: Icon, number, title, detail }) => (
            <article className="principle" key={number}>
              <div className="principle-top"><span className="icon-box"><Icon size={21} /></span><span className="principle-number">{number}</span></div>
              <h3>{title}</h3><p>{detail}</p>
              <span className="principle-line" aria-hidden="true" />
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

function DecisionContext() {
  return (
    <section className="section section-sand">
      <div className="shell context-layout">
        <div className="context-visual" aria-hidden="true">
          <div className="context-orbit" />
          <div className="context-file file-a"><span>Scope</span><strong>Problem<br />Record</strong><i /></div>
          <div className="context-file file-b"><span>Evidence</span><strong>Sources<br />linked</strong><i /></div>
          <div className="context-file file-c"><span>Review</span><strong>Decision<br />context</strong><i /></div>
          <div className="context-core"><ShieldCheck size={26} /><span>Human review<br />retained</span></div>
        </div>
        <div className="context-copy">
          <Eyebrow>Governance remains visible</Eyebrow>
          <h2>Context for review.<br /><em>Authority stays with people.</em></h2>
          <p>Broadview supports evidence-led investigation and escalation. It does not make safety, regulatory, field-action, or customer decisions on behalf of your authorized teams.</p>
          <ul className="check-list">
            <li><Check size={16} /> Evidence, scope, actions, and approval context in one working view</li>
            <li><Check size={16} /> Safety escalation and field-action review framed for responsible human decision-making</li>
            <li><Check size={16} /> Financial context presented as potential avoided exposure when assumptions support review</li>
          </ul>
        </div>
      </div>
    </section>
  );
}

function AudienceStrip() {
  const audiences = [
    ["Warranty & quality", "Ranked signals, evidence drill-through, and governed Problem Records."],
    ["Service & operations", "A shared context for cross-functional review and accountable follow-through."],
    ["Leadership", "Portfolio context, watchlists, and concise briefs tied to the same evidence posture."],
  ];
  return (
    <section className="section audience-section">
      <div className="shell">
        <Eyebrow>Built for the whole review chain</Eyebrow>
        <div className="audience-heading"><h2>Different decisions.<br /><em>One evidence posture.</em></h2><a className="text-link dark" href="/contact/">Talk to Broadview <ArrowUpRight size={16} /></a></div>
        <div className="audience-grid">
          {audiences.map(([title, detail], index) => <article key={title}><span>0{index + 1}</span><h3>{title}</h3><p>{detail}</p></article>)}
        </div>
      </div>
    </section>
  );
}

function Answers() {
  return (
    <section className="section section-answers" id="answers">
      <div className="shell answer-layout">
        <div className="answer-intro"><Eyebrow>Clear answers</Eyebrow><h2>Warranty intelligence<br /><em>without the overclaim.</em></h2><p>Broadview’s public product posture is intentionally clear about what the workspace supports and where decision authority belongs.</p><a className="button outline" href="/contact/">Contact Broadview <ArrowRight size={17} /></a></div>
        <div className="faq-list">
          {FREQUENTLY_ASKED_QUESTIONS.map(({ question, answer }) => <details key={question}><summary><span>{question}</span><CircleHelp size={18} /></summary><p>{answer}</p></details>)}
        </div>
      </div>
    </section>
  );
}

function BottomCallout() {
  return <section className="bottom-callout"><div className="shell"><span className="callout-orb" aria-hidden="true" /><div><Eyebrow>Build the review cadence</Eyebrow><h2>Make warranty work<br /><em>more legible.</em></h2></div><a className="button light" href="/contact/">Start a conversation <ArrowRight size={17} /></a></div></section>;
}

function Footer() {
  return <footer className="site-footer"><div className="shell footer-shell"><BrandMark /><p>Evidence-led warranty intelligence for accountable review.</p><div><a href="/contact/">Contact</a><a href={PORTAL_URL}>Subscriber sign in</a></div></div></footer>;
}

function HomePage() {
  return <><Header page="home" /><main><Hero /><Principles /><DecisionContext /><AudienceStrip /><Answers /><BottomCallout /></main><Footer /></>;
}

function ContactForm() {
  const [submitted, setSubmitted] = useState(false);
  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitted(true);
  }
  return (
    <form className="contact-form" onSubmit={handleSubmit}>
      <label>Your name<input name="name" autoComplete="name" required /></label>
      <label>Work email<input name="email" type="email" autoComplete="email" required /></label>
      <label>Organization<input name="organization" autoComplete="organization" required /></label>
      <label>What would you like to discuss?<textarea name="message" rows={5} required /></label>
      <button className="button primary" type="submit">Check contact request <ArrowRight size={17} /></button>
      {submitted ? <p className="form-status" role="status">This static contact form does not send or store your details yet. Please use the contact channel provided by your Broadview representative to arrange a conversation.</p> : <p className="form-note">This public preview does not collect, send, or store form entries.</p>}
    </form>
  );
}

function ContactPage() {
  return <><Header page="contact" /><main className="contact-page"><section className="contact-hero shell"><div><Eyebrow>Contact Broadview</Eyebrow><h1>Let’s make the warranty<br /><em>review process clearer.</em></h1><p>Tell us about the evidence, workflow, or leadership-context challenge your team is working through. This public preview keeps contact intake deliberately non-persistent.</p><div className="contact-aside"><ShieldCheck size={20} /><span>Public marketing and subscriber access stay separate. Internal administration is not linked from this site.</span></div></div><div className="contact-panel"><p className="panel-label">Request a conversation</p><h2>Start with the question.</h2><ContactForm /></div></section></main><Footer /></>;
}

function NotFoundPage() {
  return <><Header page="not-found" /><main className="not-found shell"><Eyebrow>404</Eyebrow><h1>This page is not in view.</h1><p>Return to Broadview’s public introduction or contact page.</p><a className="button primary" href="/">Return home <ArrowRight size={17} /></a></main><Footer /></>;
}

export function App() {
  const page = useMemo(currentPage, []);
  return page === "home" ? <HomePage /> : page === "contact" ? <ContactPage /> : <NotFoundPage />;
}
